import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { productCatalog } from '../mock-products';
import { useLanguage } from '../i18n/LanguageContext';
import ProductDetailModal from '../components/ProductDetailModal';

const ProductCategory = () => {
  const { categorySlug } = useParams();
  const { language, t } = useLanguage();
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const category = productCatalog[categorySlug];

  if (!category) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Category Not Found</h1>
          <Link to="/products">
            <Button className="bg-[#0693E3] hover:bg-[#0578BE]">
              <ArrowLeft className="mr-2" size={20} />
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = language === 'id' ? category.nameId : category.name;
  const categoryDescription = language === 'id' ? category.descriptionId : category.description;

  return (
    <div className="min-h-screen pt-20">
      {/* Breadcrumb & Header */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
            <Link to="/products" className="hover:text-[#0693E3] transition-colors">
              {t('nav.products')}
            </Link>
            <ChevronRight size={16} />
            <span className="text-gray-900 font-medium">{categoryName}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">{categoryName}</h1>
              <p className="text-xl text-gray-600 max-w-3xl">{categoryDescription}</p>
            </div>
            <Link to="/products">
              <Button variant="outline" className="border-gray-300 hover:border-[#0693E3] hover:text-[#0693E3]">
                <ArrowLeft className="mr-2" size={20} />
                {language === 'id' ? 'Kembali' : 'Back'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sub-Categories Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {category.subCategories.map((subCategory) => {
              const productName = language === 'id' ? subCategory.nameId : subCategory.name;
              const productDesc = language === 'id' ? subCategory.descriptionId : subCategory.description;
              const hasSubItems = subCategory.subItems && subCategory.subItems.length > 0;
              const hasFilters = subCategory.filters && Object.keys(subCategory.filters).length > 0;
              const hasBrands = subCategory.brands && subCategory.brands.length > 0;
              
              return (
                <Card 
                  key={subCategory.id}
                  className="group overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100"
                  onClick={() => setSelectedProduct(subCategory)}
                >
                  {/* Image Header */}
                  {subCategory.image && (
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={subCategory.image}
                        alt={productName}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4">
                        <h3 className="text-lg font-bold text-white group-hover:translate-x-1 transition-transform">
                          {productName}
                        </h3>
                      </div>
                      {/* View indicator */}
                      <div className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ChevronRight className="text-[#0693E3]" size={18} />
                      </div>
                    </div>
                  )}
                  
                  <div className="p-5">
                    {/* Title (if no image) */}
                    {!subCategory.image && (
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#0693E3] transition-colors">
                          {productName}
                        </h3>
                        <div className="w-8 h-8 bg-[#0693E3]/10 rounded-full flex items-center justify-center group-hover:bg-[#0693E3] transition-colors">
                          <ChevronRight className="text-[#0693E3] group-hover:text-white" size={16} />
                        </div>
                      </div>
                    )}
                    
                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {productDesc}
                    </p>
                    
                    {/* Brands preview */}
                    {hasBrands && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">
                          {language === 'id' ? 'Merek' : 'Brands'}
                        </p>
                        <p className="text-xs text-[#0693E3] font-medium">
                          {subCategory.brands.slice(0, 4).join(' • ')}
                          {subCategory.brands.length > 4 && ` +${subCategory.brands.length - 4}`}
                        </p>
                      </div>
                    )}
                    
                    {/* Sub-items if available */}
                    {hasSubItems && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1.5">
                          {language === 'id' ? 'Jenis' : 'Types'}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {subCategory.subItems.slice(0, 3).map((item, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                              {item}
                            </span>
                          ))}
                          {subCategory.subItems.length > 3 && (
                            <span className="px-2 py-0.5 bg-[#0693E3]/10 text-[#0693E3] text-xs rounded">
                              +{subCategory.subItems.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Filters indicator if available */}
                    {hasFilters && (
                      <div className="flex items-center text-xs text-gray-500">
                        <Filter size={12} className="mr-1" />
                        {Object.keys(subCategory.filters).length} {language === 'id' ? 'spesifikasi' : 'specs'}
                      </div>
                    )}
                  </div>
                  
                  {/* Bottom accent */}
                  <div className="h-1 bg-gray-100 group-hover:bg-[#0693E3] transition-colors" />
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'id' ? 'Butuh Spesifikasi Khusus?' : 'Need Custom Specifications?'}
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            {language === 'id' 
              ? 'Tim teknis kami siap membantu Anda menemukan produk yang sesuai dengan kebutuhan spesifik Anda'
              : 'Our technical team is ready to help you find products that match your specific requirements'}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#CF2E2E] hover:bg-[#B02525] text-white">
              {t('nav.requestQuotation')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};

export default ProductCategory;